/**
 * Modal de configuração do painel Monitoramento Externo: técnicos (árvore Coordenador > Área > Técnicos),
 * intervalo de atualização (1–20 min) e nome do painel. Salva via PUT /api/monitoramento/externo/config.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import './MonitoramentoExternoConfigModal.css';

function getAuthHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
}

/**
 * Monta árvore: coordenadores -> áreas -> técnicos (com areaId e coordenadorId já aplicados).
 */
function buildTree(coordenadores, areas, tecnicos) {
  const coordList = (coordenadores || []).filter((c) => c && c.id != null);
  const areaList = (areas || []).filter((a) => a && a.id != null);
  const techList = (tecnicos || []).filter((t) => t && t.id != null);

  return coordList.map((coord) => {
    const coordAreas = areaList.filter((a) => String(a.coordenadorId) === String(coord.id));
    return {
      id: `coord-${coord.id}`,
      tipo: 'coordenador',
      nome: coord.nome || coord.email || `Coordenador ${coord.id}`,
      coordId: coord.id,
      areas: coordAreas.map((area) => {
        const areaTechs = techList.filter((t) => String(t.areaId) === String(area.id));
        return {
          id: `area-${area.id}`,
          tipo: 'area',
          nome: area.nome || area.name || `Área ${area.id}`,
          areaId: area.id,
          tecnicos: areaTechs.map((t) => ({ id: t.id, tipo: 'tecnico', nome: t.nome || t.name || String(t.id) }))
        };
      })
    };
  }).filter((c) => c.areas.length > 0);
}

export function MonitoramentoExternoConfigModal({ open, onClose, onSaved, panelId }) {
  const baseUrl = getApiBaseUrl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nomePainel, setNomePainel] = useState('Monitoramento Externo');
  const [intervaloMinutos, setIntervaloMinutos] = useState(2);
  const [selectedTechIds, setSelectedTechIds] = useState(() => new Set());
  const [tree, setTree] = useState([]);
  const [expandedCoord, setExpandedCoord] = useState(() => new Set());
  const [expandedArea, setExpandedArea] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [teamLoading, setTeamLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (panelId == null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/api/monitoramento/externo/config/${panelId}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success && json.data) {
        const d = json.data;
        setNomePainel(d.nome_painel || 'Monitoramento Externo');
        setIntervaloMinutos(Math.min(20, Math.max(1, d.intervalo_minutos || 2)));
        setSelectedTechIds(Array.isArray(d.tecnicos) && d.tecnicos.length > 0
          ? new Set(d.tecnicos.map(String))
          : new Set());
      }
    } catch (e) {
      setError(e.message || 'Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  }, [baseUrl, panelId]);

  const fetchTeam = useCallback(async () => {
    setTeamLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const h = getAuthHeaders();
      const [rAreas, rTechs, rAreateam, rAreacoord, rCoords] = await Promise.all([
        fetch(`${baseUrl}/api/team/areas`, { headers: h }),
        fetch(`${baseUrl}/api/team/technicians`, { headers: h }),
        fetch(`${baseUrl}/api/team/areateam`, { headers: h }),
        fetch(`${baseUrl}/api/team/areacoord`, { headers: h }),
        fetch(`${baseUrl}/api/team/coordinators`, { headers: h })
      ]);
      const areas = (await rAreas.json()).data || [];
      const tecnicos = (await rTechs.json()).data || [];
      const areateam = (await rAreateam.json()).data || [];
      const areacoord = (await rAreacoord.json()).data || [];
      const coordenadores = (await rCoords.json()).data || [];

      const areasWithCoord = areas.map((a) => {
        const v = areacoord.find((x) => String(x.id_area) === String(a.id));
        return { ...a, coordenadorId: v ? v.id_coordinator : null };
      });
      const tecnicosWithArea = tecnicos.map((t) => {
        const v = areateam.find((x) => String(x.id_tech) === String(t.id));
        return { ...t, areaId: v ? v.id_area : null };
      });
      setTree(buildTree(coordenadores, areasWithCoord, tecnicosWithArea));
    } catch (_) {
      setTree([]);
    } finally {
      setTeamLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (open && panelId != null) {
      fetchConfig();
      fetchTeam();
    }
  }, [open, panelId, fetchConfig, fetchTeam]);

  const filteredTree = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return tree;
    return tree.map((coord) => ({
      ...coord,
      areas: coord.areas.map((area) => ({
        ...area,
        tecnicos: area.tecnicos.filter((t) => t.nome.toLowerCase().includes(q))
      })).filter((area) => area.tecnicos.length > 0 || area.nome.toLowerCase().includes(q))
    })).filter((c) => c.areas.length > 0);
  }, [tree, searchQuery]);

  const toggleCoord = (coordId) => {
    setExpandedCoord((prev) => {
      const next = new Set(prev);
      if (next.has(coordId)) next.delete(coordId);
      else next.add(coordId);
      return next;
    });
  };

  const toggleArea = (areaKey) => {
    setExpandedArea((prev) => {
      const next = new Set(prev);
      if (next.has(areaKey)) next.delete(areaKey);
      else next.add(areaKey);
      return next;
    });
  };

  const toggleTech = (techId) => {
    const id = String(techId);
    setSelectedTechIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCoordSelection = (coord) => {
    const allIds = coord.areas.flatMap((a) => a.tecnicos.map((t) => String(t.id)));
    const anySelected = allIds.some((id) => selectedTechIds.has(id));
    setSelectedTechIds((prev) => {
      const next = new Set(prev);
      if (anySelected) allIds.forEach((id) => next.delete(id));
      else allIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleAreaSelection = (area) => {
    const allIds = area.tecnicos.map((t) => String(t.id));
    const anySelected = allIds.some((id) => selectedTechIds.has(id));
    setSelectedTechIds((prev) => {
      const next = new Set(prev);
      if (anySelected) allIds.forEach((id) => next.delete(id));
      else allIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleIntervalChange = (e) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v)) setIntervaloMinutos(Math.min(20, Math.max(1, v)));
  };

  const handleSave = async () => {
    if (panelId == null) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/api/monitoramento/externo/config/${panelId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nome_painel: nomePainel.trim() || 'Monitoramento Externo',
          intervalo_minutos: intervaloMinutos,
          tecnicos: selectedTechIds.size > 0 ? Array.from(selectedTechIds) : []
        })
      });
      const json = await res.json();
      if (json.success) {
        onSaved && onSaved();
        onClose();
      } else {
        setError(json.message || 'Erro ao salvar');
      }
    } catch (e) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async () => {
    if (panelId == null) return;
    if (!window.confirm('Excluir este painel? Ele será removido da lista.')) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/api/monitoramento/externo/config/${panelId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (json.success) {
        onSaved && onSaved();
        onClose();
      } else {
        setError(json.message || 'Erro ao excluir');
      }
    } catch (e) {
      setError(e.message || 'Erro ao excluir');
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="monitoramento-externo-config-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="monitoramento-externo-config-title">
      <div className="monitoramento-externo-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="monitoramento-externo-config-header">
          <h2 id="monitoramento-externo-config-title">Configuração do painel</h2>
          <button type="button" className="monitoramento-externo-config-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <div className="monitoramento-externo-config-body">
          {loading && (
            <div className="monitoramento-externo-config-loading">Carregando configuração…</div>
          )}
          {error && (
            <div className="monitoramento-externo-config-error" role="alert">{error}</div>
          )}

          <div className="monitoramento-externo-config-field">
            <label htmlFor="monitoramento-externo-config-nome">Nome do painel</label>
            <input
              id="monitoramento-externo-config-nome"
              type="text"
              value={nomePainel}
              onChange={(e) => setNomePainel(e.target.value)}
              placeholder="Monitoramento Externo"
              className="monitoramento-externo-config-input"
            />
          </div>

          <div className="monitoramento-externo-config-field">
            <label htmlFor="monitoramento-externo-config-intervalo">Atualização (minutos)</label>
            <input
              id="monitoramento-externo-config-intervalo"
              type="number"
              min={1}
              max={20}
              value={intervaloMinutos}
              onChange={handleIntervalChange}
              className="monitoramento-externo-config-input monitoramento-externo-config-input-num"
            />
            <span className="monitoramento-externo-config-hint">1 a 20 minutos</span>
          </div>

          <div className="monitoramento-externo-config-field">
            <label>Técnicos no painel</label>
            <input
              type="text"
              placeholder="Buscar coordenador, área ou técnico..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="monitoramento-externo-config-input monitoramento-externo-config-search"
            />
            <div className="monitoramento-externo-config-tree">
              {teamLoading && <div className="monitoramento-externo-config-loading">Carregando equipe…</div>}
              {!teamLoading && filteredTree.length === 0 && (
                <div className="monitoramento-externo-config-empty">Nenhum técnico vinculado a áreas/coordenadores ou nenhum resultado na busca.</div>
              )}
              {!teamLoading && filteredTree.map((coord) => {
                const isCoordExpanded = expandedCoord.has(coord.id);
                const coordTechIds = coord.areas.flatMap((a) => a.tecnicos.map((t) => String(t.id)));
                const coordChecked = coordTechIds.length > 0 && coordTechIds.every((id) => selectedTechIds.has(id));
                const coordIndet = coordTechIds.some((id) => selectedTechIds.has(id)) && !coordChecked;
                return (
                  <div key={coord.id} className="monitoramento-externo-config-tree-group">
                    <div className="monitoramento-externo-config-tree-row monitoramento-externo-config-tree-coord">
                      <button type="button" className="monitoramento-externo-config-tree-chevron" onClick={() => toggleCoord(coord.id)} aria-expanded={isCoordExpanded}>
                        {isCoordExpanded ? '▼' : '▶'}
                      </button>
                      <label className="monitoramento-externo-config-tree-label">
                        <input
                          type="checkbox"
                          checked={coordChecked}
                          ref={(el) => { if (el) el.indeterminate = coordIndet; }}
                          onChange={() => toggleCoordSelection(coord)}
                        />
                        <span className="monitoramento-externo-config-tree-text">{coord.nome}</span>
                      </label>
                    </div>
                    {isCoordExpanded && (
                      <div className="monitoramento-externo-config-tree-children">
                        {coord.areas.map((area) => {
                          const areaKey = `area-${area.areaId}`;
                          const isAreaExpanded = expandedArea.has(areaKey);
                          const areaChecked = area.tecnicos.length > 0 && area.tecnicos.every((t) => selectedTechIds.has(String(t.id)));
                          const areaIndet = area.tecnicos.some((t) => selectedTechIds.has(String(t.id))) && !areaChecked;
                          return (
                            <div key={areaKey} className="monitoramento-externo-config-tree-group">
                              <div className="monitoramento-externo-config-tree-row monitoramento-externo-config-tree-area">
                                <button type="button" className="monitoramento-externo-config-tree-chevron" onClick={() => toggleArea(areaKey)} aria-expanded={isAreaExpanded}>
                                  {isAreaExpanded ? '▼' : '▶'}
                                </button>
                                <label className="monitoramento-externo-config-tree-label">
                                  <input
                                    type="checkbox"
                                    checked={areaChecked}
                                    ref={(el) => { if (el) el.indeterminate = areaIndet; }}
                                    onChange={() => toggleAreaSelection(area)}
                                  />
                                  <span className="monitoramento-externo-config-tree-text">{area.nome}</span>
                                  <span className="monitoramento-externo-config-tree-count">{area.tecnicos.length}</span>
                                </label>
                              </div>
                              {isAreaExpanded && (
                                <div className="monitoramento-externo-config-tree-children">
                                  {area.tecnicos.map((t) => (
                                    <label key={t.id} className="monitoramento-externo-config-tree-row monitoramento-externo-config-tree-tech">
                                      <span className="monitoramento-externo-config-tree-chevron" />
                                      <input
                                        type="checkbox"
                                        checked={selectedTechIds.has(String(t.id))}
                                        onChange={() => toggleTech(t.id)}
                                      />
                                      <span className="monitoramento-externo-config-tree-text">{t.nome}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="monitoramento-externo-config-footer">
          <button type="button" className="monitoramento-externo-config-btn monitoramento-externo-config-btn-delete" onClick={handleExcluir} disabled={deleting}>
            {deleting ? 'Excluindo…' : 'Excluir'}
          </button>
          <div className="monitoramento-externo-config-footer-actions">
            <button type="button" className="monitoramento-externo-config-btn monitoramento-externo-config-btn-cancel" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="monitoramento-externo-config-btn monitoramento-externo-config-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando…' : 'Atualizar dados'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
